import { DatePipe } from '@angular/common';
import {AbstractControl, Form, FormArray, FormControl, FormGroup, UntypedFormGroup} from '@angular/forms';
import { DATE_MAX_STR, DATE_MIN_STR, M_D_YYYY, UTC } from '../constants/constants';
import { AuditTypesEnum } from '../interfaces/IAudit';
import Decimal from 'decimal.js';

export const getFormattedDateString = (originalDate: Date, format?: string) => {
  if (!originalDate) {
    return '';
  }

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December',
  ];
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };
  const isYesterday = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);

    yesterday.setDate(yesterday.getDate() - 1);

    today.toDateString();
    yesterday.toDateString();

    return date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();
  };
  const getAdaptiveDayOfMonth = (d: Date, mod: string): string => {
    const now = new Date().getTime();
    const year = d.getFullYear();
    const month = months[d.getMonth()].slice(0, 3);
    const day = d.getDate();
    const hours = d.getHours() > 9 ? d.getHours() : '0' + d.getHours();
    const minutes = d.getMinutes() > 9 ? d.getMinutes() : '0' + d.getMinutes();
    const diffTime = Math.abs(now - d.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffYears = new Date().getFullYear() - year;

    let prefixDate;

    if (isToday(d)) {
      prefixDate = 'Today';
    } else if (isYesterday(d)) {
      prefixDate = 'Yesterday';
    } else {
      prefixDate = `${month} ${day}`;
    }

    switch (mod) {
      case 'MM d a, HH:mm':
        return prefixDate + `, ${hours}:${minutes}`;
      case 'MM d a, yyyy':
        return prefixDate + (diffYears > 0 ? year : '');
    }

    return prefixDate + `${hours}:${minutes}`;
  };

  let formattedDate = '';

  switch (format) {
    case 'MM/dd/yyyy':
      formattedDate = `${originalDate.getMonth() + 1}/${originalDate.getDate()}/${originalDate.getFullYear()}`;
      break;
    case 'MMM dd, yyyy':
      formattedDate = `${months[originalDate.getMonth()]} ${originalDate.getDate()}, ${originalDate.getFullYear()}`;
      break;
    case 'MM d a, HH:mm':
      formattedDate = getAdaptiveDayOfMonth(originalDate, format);
      break;
    case 'MM d a, yyyy':
      formattedDate = getAdaptiveDayOfMonth(originalDate, format);
      break;
    default:
      formattedDate = `${days[originalDate.getDay()]}, ${months[originalDate.getMonth()]} ${originalDate.getDate()}`;
      break;
  }

  return formattedDate;
};

export const getDateFromISO = (ISO: string): Date => {
  if (ISO) {
    return new Date(ISO);
  }

  return null;
};

export const sortByProperty = (array: any[], property: string, asc: boolean) => {
  const sortOrder = asc ? 1 : -1;

  const sortFunction = (a: any, b: any) => {
    let result;

    // sort by the property or find in nested objects
    if (a[property]) {
      result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
    } else {
      const aFlat = getFlatObject(a);
      const bFlat = getFlatObject(b);

      result = (aFlat[property] < bFlat[property]) ? -1 : (aFlat[property] > bFlat[property]) ? 1 : 0;
    }
    
    if(a[property] === null) return 1;
    if(b[property] === null) return -1;

    return result * sortOrder;
  };

  return array?.sort(sortFunction);
};

// e.g.
// input: obj === {nested: {aChild: 3, bChild: 4}, some: 'test'}
// output: obj === {'nested.aChild': 3, 'nested.bChild': 4, some: 'test'}
// use: myObj['nested.aChild'] = 33
// to make sure all properties are unique
export const getFlatObject = (input) => {
  const flat = (res, key, val, pre = '') => {
    const prefix = [pre, key].filter(v => v).join('.');
    return (val && typeof val === 'object')
      ? Object.keys(val).reduce((prev, curr) => flat(prev, curr, val[curr], prefix), res)
      : Object.assign(res, {[prefix]: val});
  };

  return Object.keys(input).reduce((prev, curr) => flat(prev, curr, input[curr]), {});
};

export const objectToQuerystring = (name: string, o = {}) =>
  Object.entries(o).map((p: [string, any]) => {
    let value = p[1];
    if (p[1] == null || p[1] === undefined) {
      value = '';
    }
    return (`${name}.${encodeURIComponent(p[0])}=${encodeURIComponent(value)}`);
  })
    .join('&');

const isFormGroup = (control: AbstractControl): control is UntypedFormGroup => {
  return !!(control as UntypedFormGroup).controls;
};

export const collectErrors = (control: AbstractControl): any | null => {
  if (isFormGroup(control)) {
    return Object.entries(control.controls)
      .reduce(
        (acc, [key, childControl]) => {
          const childErrors = collectErrors(childControl);
          if (childErrors) {
            acc = {...acc, [key]: childErrors};
          }
          return acc;
        },
        null
      );
  } else {
    return control.errors;
  }
};

export const isSubstringOfElement = (element, sbtr) => element?.includes(sbtr);

export function uniqueBy(a: any[], key: any): any[] {
  return [
    ...new Map(
      a?.map(x => [key(x), x])
    ).values()
  ];
}

export function generateUUID(): string {
  let d = new Date().getTime();
  let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export function getFormattedDateRangeString(from?: string, to?: string, format?: string, fromPrefix?: string, toPrefix?: string, fromSuffix?: string, toSuffix?: string) {
  format??= M_D_YYYY;
  if(from && to && isValidDate(from) && isValidDate(to)) {
    const datePipe = new DatePipe(navigator.language);
    const fromStr  = datePipe.transform(from, format, UTC);
    const toStr = datePipe.transform(to, format, UTC);

    if(fromStr.includes(DATE_MIN_STR) || toStr.includes(DATE_MAX_STR))
      return '';
      
    const today = new Date().toDateString();
    const parsedDate = new Date(from).toDateString();
   
    function transformString(str: string) {
      return str ? ` ${str} ` : '';
    }

    if(today === parsedDate) {
      return `${transformString(fromPrefix)}${fromStr}${transformString(fromSuffix)}${transformString(toPrefix)}${fromStr}${transformString(toSuffix)}`;
    }
 
    return`${transformString(fromPrefix)}${fromStr}${transformString(fromSuffix)}${transformString(toPrefix)}${toStr}${transformString(toSuffix)}`; 
  }
  return '';
}

export function todayAtMidnight(): Date {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

export function getUTC(date: string | Date): Date | null {
 return date ? new Date(new Date(date )?.toLocaleString('en-US', { timeZone: 'UTC' })) : null;
}

export function isMinDate(date: Date): boolean {
  return date.toString() === '0001-01-01T00:00:00' || date.toString() === '0001-01-01T00:00:00+00:00';
}

export function deepCopy<T>(obj: T): T {
  return JSON?.parse(JSON?.stringify(obj || []));
}

export function groupByProperty(array: any[], key: string, totalPerGroupKey: string = null): any[] {
  const sortObjByProperty = array.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
  if (!totalPerGroupKey) {
    return Object.entries(sortObjByProperty).map(([k, v]) => ({header: k, transactions: v}));
  }
  return Object.entries(sortObjByProperty).map(([k, v]) => ({header: k, records: v, 
    totalPerGroup: (v as any[])?.map(x => x?.[totalPerGroupKey])?.reduce((accumulator, currentValue) => new Decimal(accumulator).add(new Decimal(currentValue)))
  }));
}

export function getAuditTypeString(type: AuditTypesEnum): string {
  switch (type) {
      case AuditTypesEnum.ActiveDrugs:
          return 'Active Drugs Audit';
      case AuditTypesEnum.AdHoc:
          return 'Ad Hoc Audit';
      case AuditTypesEnum.InactiveDrugs:
          return 'Inactive Drugs Audit';
      case AuditTypesEnum.AllDrugs:
          return 'All Drugs Audit';
      case AuditTypesEnum.NinetyDayAudit:
          return 'Ninety Day Audit';
      case AuditTypesEnum.BackCount:
        return 'Back Count';
      default:
          return '';
  }
}

export function downloadFile(blob: Blob, fileName: string): void {
  const downloadedFile = new Blob([blob], {type: blob.type});
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.download = fileName;
  a.href = URL.createObjectURL(downloadedFile);
  a.target = '_blank';
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

export function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
}

export function isEqual(obj1, obj2): boolean {
  function sortKeys(obj) {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(sortKeys);
    }
    return Object.keys(obj).sort().reduce((result, key) => {
        result[key] = sortKeys(obj[key]);
        return result;
    }, {});
}
  return JSON.stringify(sortKeys(obj1)) === JSON.stringify(sortKeys(obj2));
}

export function focusInput(appInputViewChild: any): void {
    setTimeout(() => appInputViewChild && appInputViewChild?.appInput?.nativeElement?.focus());
}

export function focusButton(buttonViewChild: any): void {
    setTimeout(() => buttonViewChild?.focus());
}

export function filterTable(data: any[], filterValue: string, ...properties: string[]) {
    if (properties.length === 0) {
        return data;
    }
    filterValue = filterValue.toLowerCase();

    const matchesFilter = (item): boolean => {
        return properties.some((prop) => ((item[prop]).toLowerCase())?.indexOf(filterValue) > -1);
    };

    return data.filter(matchesFilter);
}

export function highlightRequiredControlsWithErrors(form: FormGroup): void {
  if (form?.controls) {
    Object.keys(form.controls)?.forEach((controlName: string) => {
      const control = form.get(controlName);
      if (control instanceof FormGroup) { 
        highlightRequiredControlsWithErrors(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(group => {
          if (group instanceof FormGroup) {
            highlightRequiredControlsWithErrors(group);
          }
        });
      }
      else if (control instanceof FormControl) {
          // Check if the control is required and empty
        if (control?.invalid) {
          control.markAsTouched();
          control.markAsDirty();
          control.updateValueAndValidity();
        }
      }
    });
  }
}


export function getNestedProperty(obj, path) {
  return path.split('.').reduce((acc, key) => acc && acc[key], obj);
}

export function valueIsNumber(value: any): boolean {
  return value?.toString()?.length && !isNaN(value);
}

export function getBase64FromDataUrl(dataUrl: string): string {
  if (!dataUrl)
    return null;
  
  const base64String = dataUrl.split(',')[1];
  return base64String;
}

export function equalsCore(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !equalsCore(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}
